import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPaymentStatus } from '@/lib/payments';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Star, Crown } from 'lucide-react';

const PAID_TIERS = ['growth', 'premium', 'elite'];

// Paths that remain reachable even when paywall is required
const PAYWALL_ALLOW_PATHS = ['/home', '/school-materials', '/subscription', '/profile', '/auth', '/login', '/onboarding', '/terms', '/privacy', '/refund', '/shipping', '/contact', '/learn', '/'];

/**
 * Wraps the app and redirects students/parents past their trial end-date who
 * haven't paid yet to /subscription. Admin & support_staff always bypass.
 */
export default function PaywallGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user) return;
    // CRITICAL: Skip during OAuth callback — AuthCallback is mid-flight and
    // will navigate to /home or /onboarding as soon as it finishes. Firing
    // fetchPaymentStatus here races with the AuthCallback navigate and can
    // bounce fresh signups back to /login.
    if (location.hash?.includes('session_id=')) return;
    if (['admin', 'support_staff'].includes((user.role || '').toLowerCase())) return;
    fetchPaymentStatus().then(setStatus).catch(() => {});
  }, [user, location.pathname, location.hash]);

  useEffect(() => {
    if (location.hash?.includes('session_id=')) return;
    if (!status?.paywall_required) return;
    const path = location.pathname || '/';
    const allowed = PAYWALL_ALLOW_PATHS.some(p => p === path || path.startsWith(p + '/') || (p === '/' && path === '/'));
    if (!allowed) {
      navigate('/subscription', { replace: true, state: { paywall: true } });
    }
  }, [status, location.pathname, location.hash, navigate]);

  // Gentle, dismissible soft nudge — shown at most ONCE PER DAY, and only
  // after the user has spent real time using the app (never on immediate load).
  // Absolutely no forcing: users keep full free access until the paywall date.
  const [showPrompt, setShowPrompt] = useState(false);
  useEffect(() => {
    if (!status || !user) return;
    if (!user.study_mode || !user.onboarding_complete || !user.section) return;
    if (status.paywall_required) return;               // on/after 1 Sep the hard paywall handles it
    if (['admin', 'support_staff'].includes((user.role || '').toLowerCase())) return;
    if (PAID_TIERS.includes((status.tier || '').toLowerCase())) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem('gv_subscribe_nudge_day') === today) return;  // already shown today
    const path = location.pathname || '/';
    if (['/subscription', '/onboarding', '/login', '/auth'].some(p => path.startsWith(p))) return;
    // Show only after ~2 minutes of active use, so it feels like a friendly
    // reminder after they've enjoyed the features — not a gate.
    const t = setTimeout(() => setShowPrompt(true), 120000);
    return () => clearTimeout(t);
  }, [status, user, location.pathname]);

  const dismissPrompt = () => {
    localStorage.setItem('gv_subscribe_nudge_day', new Date().toISOString().slice(0, 10));
    setShowPrompt(false);
  };

  return (
    <>
      {children}
      <Dialog open={showPrompt} onOpenChange={(o) => { if (!o) dismissPrompt(); }}>
        <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden" data-testid="subscribe-prompt">
          <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-emerald-500 px-6 py-7 text-white">
            <h2 className="font-heading text-2xl font-bold">Enjoying Gurukul Veda? 💛</h2>
            <p className="text-sm text-white/90 mt-1.5">If Gurukul Veda is helping you learn, lock in the offer before prices go up. No rush — keep using everything free{status?.trial_end_label ? ` until ${status.trial_end_label}` : ''}.</p>
          </div>
          <div className="p-6 space-y-3">
            <button onClick={() => { dismissPrompt(); navigate('/subscription'); }} data-testid="prompt-choose-growth"
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 transition-colors text-left">
              <Star className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-heading font-bold text-slate-800">Growth — ₹249/month</p>
                <p className="text-xs text-slate-500">Unlimited chat, quizzes, voice & parent access.{status?.trial_end_short ? ` Billed only from ${status.trial_end_short}.` : ''}</p>
              </div>
            </button>
            <button onClick={() => { dismissPrompt(); navigate('/subscription'); }} data-testid="prompt-choose-lifetime"
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-purple-200 bg-purple-50 hover:border-purple-400 transition-colors text-left">
              <Crown className="w-6 h-6 text-purple-600 flex-shrink-0" />
              <div>
                <p className="font-heading font-bold text-slate-800">Elite Lifetime — ₹5,999 once</p>
                <p className="text-xs text-slate-500">Everything, forever. Independence Day offer — first 499 students only.</p>
              </div>
            </button>
            <button onClick={dismissPrompt} data-testid="prompt-dismiss"
              className="w-full text-center text-sm text-slate-400 hover:text-slate-600 font-body py-1 mt-1">
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
